import React, { createContext, useContext, useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn as amplifySignIn, signOut as amplifySignOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from '../aws-config';

// Configure Amplify
Amplify.configure(awsConfig);

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState(null);

  useEffect(() => {
    // Check if user is already authenticated
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      const userData = {
        username: currentUser.username,
        userId: currentUser.userId,
        signedInAt: new Date().toISOString(),
      };
      
      setUser(userData);
      setIdToken(session.tokens?.idToken?.toString());
    } catch (error) {
      // User is not authenticated
      setUser(null);
      setIdToken(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (username, password) => {
    try {
      const { isSignedIn, nextStep } = await amplifySignIn({ username, password });
      
      if (isSignedIn) {
        await checkAuthStatus();
        return { success: true };
      }
      
      // Handle additional steps (e.g., password change required)
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { 
          success: false, 
          error: 'Password change required',
          requiresNewPassword: true,
          nextStep 
        };
      }
      
      return { success: false, error: 'Sign in incomplete' };
    } catch (error) {
      console.error('Sign in error:', error);
      
      let errorMessage = 'Failed to sign in';
      
      if (error.name === 'NotAuthorizedException') {
        errorMessage = 'Incorrect username or password';
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = 'User not found';
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = 'User account not confirmed';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const signOut = async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setIdToken(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const getIdToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, idToken, getIdToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
